import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormControlStatus, FormGroup } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatChipGrid } from '@angular/material/chips';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { isAssetRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { IRelatedEntity, RelatedEntity } from '@app/core/model/entities/entity';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AbstractFieldBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { getValue } from '@app/shared/extra/utils';
import { BehaviorSubject, combineLatest, EMPTY, exhaustMap, merge, Observable, pipe, Subject, takeWhile } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';

@Component({template: '', standalone: false})
export abstract class EntityChipsFieldBuilderComponent<RE extends RelatedEntity> extends AbstractFieldBuilder<FormControl<string>> implements OnInit, AfterViewInit {

  protected abstract readonly entityType: EntityTypeEnum;
  protected abstract readonly viewPermission: PermissionEnum;

  protected requiresAsset = true;
  public noAsset = true;
  public hasRelatedEntities = false;
  protected abstract noRelatedEntitiesMessage: string;
  protected assetTypeAllowsEntityModule = false;
  protected viewportHeight: string = '0px';

  private scrollTrigger$ = new Subject<number>();

  // List of selected items in the field
  public selectedEntities = new BehaviorSubject<RE[]>([]);
  // List of loaded suggestions
  public loadedEntities = new BehaviorSubject<RE[]>([]);
  // List of available suggestions, filtered by input search and absence from this.selectedEntities
  public filteredOptions$: Observable<RE[]>;

  public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  public Permission = PermissionEnum;
  protected assetChanged$ = new BehaviorSubject<string>(null);

  @ViewChild('inputField') public inputField: ElementRef<HTMLInputElement>;
  @ViewChild('suggestions') public suggestions: MatAutocomplete;
  @ViewChild('chipList') public chipList: MatChipGrid;

  /**
   * Create the form, add the chips and hook to the asset value change to update the chip list.
   */
  public ngOnInit(): void {
    // Initialise form
    this.selectedEntities.next(this.getFieldValue());
    this.form = new FormGroup({
      field: new FormControl('', this.computeValidators({values: this.selectedEntities.value}))
    });

    // Propagate invalid status to the mat-chip-list
    this.form.controls.field.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status: FormControlStatus) => {
        if (this.chipList !== void 0) {
          this.chipList.errorState = status === 'INVALID';
        }
      });

    // this.viewToggled and this.editToggled are initialised but never re-written
    // Use their value to initialise a local variable which can change over time
    this.assetTypeAllowsEntityModule = this.viewToggled && this.editToggled;

    // When either the loaded or selected entities change, filter to keep unselected entities
    this.filteredOptions$ = combineLatest([this.loadedEntities, this.selectedEntities]).pipe(
      map(([entities, selectedEntities]) =>
        entities.filter(e => !selectedEntities.map(c => c.id).includes(e.id))
      )
    );

    // Reusable pipeline
    const reloadNewPage = pipe(
      debounceTime(500),
      filter(() =>
        (this.currentMode === FieldStateMode.EDIT || this.isCreatingEntity)
        && this.accessManager.hasAccess(this.viewPermission)
      ),
      tap(() => this.loadedEntities.next([])),
      switchMap(() => this.scrollTrigger$.pipe(
          startWith(0),
          distinctUntilChanged(),
          // Reject scroll indices until the threshold is reached
          filter(index => index >= this.loadedEntities.value.length - this.appConfig.PRELOAD_THRESHOLD),
          // Ignore all new inputs until the api call completes
          exhaustMap(() => this.loadEntitiesNextPage(this.loadedEntities.value.length)),
          // Emit the accumulation of existing data and the new page
          tap(entitiesNewPage => this.loadedEntities.next([...this.loadedEntities.value, ...entitiesNewPage])),
          // Unsubscribe once the final page is received
          takeWhile(projectsNewPage => projectsNewPage.length === this.appConfig.PREFERRED_REQUEST_PAGE_SIZE, true)
        )
      )
    );

    /*
    For each change of asset or change of input in the field control,
    fetch the first entities page corresponding to the new displayName filter
     */
    merge(
      this.assetChanged$.pipe(
        filter(assetId => !!assetId),
        reloadNewPage
      ),
      this.form.controls.field.valueChanges.pipe(
        reloadNewPage
      ),
      this.getFieldGroupState().pipe(
        filter(newMode => newMode === FieldStateMode.EDIT),
        reloadNewPage
      ),
      this.formStateService.loaded$.pipe(
        filter(() => !this.requiresAsset),
        reloadNewPage
      )
    )
      .pipe(
        tap(() => {
          // Since we can't tell if the field having no suggestions is the result of the filter or not,
          // the field is said to have related entities if there are loaded entities or something was input in the search filter
          this.hasRelatedEntities = this.loadedEntities.value.length > 0 || !!this.form.controls.field.value;
          this.focusable = this.hasRelatedEntities;
          const count = Math.min(this.loadedEntities.value.length, this.appConfig.CDK_VIEWPORT_MAXIMUM_ITEM_COUNT);
          this.viewportHeight = `${count * this.appConfig.CDK_VIEWPORT_ITEM_HEIGHT}px`;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Fetch entities' related asset and listen for its changes
    this.formStateService.loaded$
      .pipe(
        filter(() => this.requiresAsset),
        switchMap(() => {
          // FIXME Combine this.assetChanged$, and therefore the entire merge() with this getFormChanges(asset)
          if (this.getFormChanges(['asset'])) {
            // If the field group contains an Asset field whose value can change, emit asset id on assetChangedSubject whenever a new Asset is selected
            return this.getFormChanges(['asset'])
              .pipe(
                // Verify uniqueness by asset Id
                distinctUntilChanged((previous, current) => previous.value?.id === current.value?.id),
                tap(({value}) => {
                  this.assetTypeAllowsEntityModule = value?.typeModules?.includes(this.entityType);

                  const relatedEntities = value?.id === this.entity?.['assetId'] ? this.getFieldValue() : [];
                  this.setFieldValue(relatedEntities);
                  this.selectedEntities.next(relatedEntities);

                  this.noAsset = !value?.id;
                  this.assetChanged$.next(value?.id);
                })
              );
          } else {
            // Otherwise, load the entity's related Asset only once, if applicable.
            // Form does not contain an Asset field
            this.noAsset = false;

            if (!!this.appManager.currentAsset) {
              this.assetChanged$.next(this.appManager.currentAsset.id);
            } else if (isAssetRelatedEntity(this.entity) && this.entity.relatedAssets.length > 0) {
              this.assetChanged$.next(this.entity.relatedAssets[0].id);
            }

            return EMPTY;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Initialise the field in the registry
    this.setFieldValue(this.fieldInitValue);
    this.setFieldInitialValue(this.fieldInitValue);
    this.getNextState();
  }

  /**
   * Load entities next page.
   * @param startingIndex Start of the page to load.
   * @return Observable<RE[]> A list of related entities matching the form filters.
   */
  protected abstract loadEntitiesNextPage(startingIndex: number): Observable<RE[]>;

  /**
   * Whether the current User is allowed to edit this field. In addition to the field's own edition permissions,
   * editing requires view access to the related entity type, since edit mode lets the User search and browse
   * every entity of that type through the autocomplete suggestions.
   */
  protected get canEdit(): boolean {
    return this.accessManager.hasAllNeededPermissions(this.permissionsForEdition)
      && this.accessManager.hasAccess(this.viewPermission);
  }

  /**
   * Either activate the field into editing mode or save it if you were editing it. Overridden to also require
   * view access to the related entity type before entering edit mode.
   */
  protected override activate(): void {
    if (this.currentMode === this.FieldMode.EDIT && this.form.valid) {
      this.save();
    } else if (this.canEdit && this.preconditionsForEdition) {
      this.edit();
    }
  }

  /**
   * Set up hooks
   */
  public ngAfterViewInit(): void {
    this.setupHooks();
    // Reset on error
    this.getFieldGroupState().pipe(
      filter(state => state === FieldStateMode.ERROR),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.cancel());
  }

  /**
   * Get the field value to display in read mode.
   * @return RE
   */
  public getFieldValue(): RE[] {
    return getValue(this.entity, this.fieldConfig.fieldPath)
      ?.map((re: IRelatedEntity) => new RelatedEntity(re)) ?? [];
  }

  // TODO TTT-4135 Remove, iterate over errors instead of validators
  /**
   * List of validators for which an error has been raised by the corresponding validator function.
   */
  public override get erroredValidators(): FieldValidator[] {
    return this.fieldConfig.field.validators.filter(validator => !!this.form.controls.field?.errors?.[validator.code]);
  }

  /**
   * Compute the field initial value.
   * @protected
   */
  protected calcFieldInitValue(): void {
    this.fieldInitValue =
      (!this.isCreatingEntity && this.fieldConfig) ? (getValue(this.entity, this.fieldConfig.fieldPath) ?? []) : [];
  }

  /**
   * Remove a selected entity from the list.
   * @param entity The entity to be removed.
   */
  public remove(entity: RE): void {
    const selectedEntities = this.selectedEntities.value;
    const index = selectedEntities.indexOf(entity);

    if (index >= 0) {
      selectedEntities.splice(index, 1);
    }

    this.selectedEntities.next(selectedEntities);
    this.setFieldValue(selectedEntities.slice());
  }

  /**
   * Reset the form and the chips to they initial state, then switch to read mode.
   */
  public cancel(): void {
    this.selectedEntities.next((this.getFieldValue() || []).slice());
    this.form.controls.field.setValue('', {emitEvent: false});
    this.setFieldValue((this.getFieldInitialValue() || []).slice());
    this.getNextState();
  }

  /**
   * Add a chip when the user select an entry in the suggestion panel.
   * @param entity Option selected in filtered list
   * @protected
   */
  public add(entity: RE): void {
    this.inputField.nativeElement.value = '';

    // Add the new chip
    const selectedEntities = [...this.selectedEntities.value, entity];
    this.selectedEntities.next(selectedEntities);
    this.form.controls.field.setValue('', {emitEvent: false});
    this.setFieldValue(selectedEntities.slice());
  }

  /**
   * Set focus on the element
   */
  public focus(): void {
    if (this.focusable) {
      setTimeout(() => this.inputField?.nativeElement?.focus());
    }
  }

  /**
   * Emit the index of the top item displayed in the viewport.
   * @param index Scroll index.
   */
  public onScroll(index: number) {
    this.scrollTrigger$.next(index);
  }

  /**
   * Navigate to related entity sheet
   * @param relatedEntityId
   */
  public abstract navigateToRelatedEntitySheet(relatedEntityId: string): Promise<void>;
}
